import { useSiteStore, ROLE_RANK } from "../stores/siteStore";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listInventoryRuns, listInventoryMaps, uploadInventoryBundle, uploadExpectedStock, uploadProductAliases,
  listExpectedStockSnapshots,
  uploadStockMovements,
  type InventoryReject, type InventoryRunSummary,
} from "../api/rest";
import { rejectReasonLabel } from "./rejectReasons";
import { InventoryTrendSection } from "./InventoryTrendSection";
import { LiveCountBanner } from "../panels/LiveCountBanner";
import { useSiteId } from "../hooks/useSiteId";
import { useWarehouseId } from "../hooks/useWarehouseId";
import { useTableSort, type SortAccessor } from "../app/table/useTableSort";
import { SortableTh } from "../app/table/SortableTh";
// Shared page/table shell (.page, .h1, .tableScroll, .table, .mono, .empty,
// .error) is reused from FleetPage.module.css, same as AlertsPage/OeePage —
// see those files for the convention.
import styles from "./FleetPage.module.css";
import inv from "./InventoryPage.module.css";

// Header sorting (ADR 151): accessors live at module level so the sort memo
// only recomputes on data/direction changes. Name mirrors the cell's fallback
// (name ?? id); dates compare as epoch, not display text.
const NO_RUNS: InventoryRunSummary[] = [];
const RUN_COLS: Record<"name" | "addresses" | "status" | "imported" | "wms", SortAccessor<InventoryRunSummary>> = {
  // ADR 383: the WMS snapshot the run is judged against, as a date.
  wms: (r) => (r.expectedAsOf ? new Date(r.expectedAsOf).getTime() : null),
  name: (r) => r.name ?? r.id,
  addresses: (r) => r.addressScanned,
  status: (r) => r.status,
  imported: (r) => Date.parse(r.createdAt),
};

export function InventoryPage() {
  const { t } = useTranslation();
  const siteId = useSiteId();
  // ADR 394: every import here is a site-admin write (assertWriteAccess); a supervisor
  // offered the buttons only meets a 403. They read and review, so the row is not drawn.
  const canImport = useSiteStore((st) => st.isGlobalAdmin || st.roleRank >= ROLE_RANK["site-admin"]);
  const warehouseId = useWarehouseId();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Map name for the bundle (ADR 137): without it run.mapName stays null and the
  // coverage/aisle KPIs never resolve a denominator (ADR 122 semantics).
  const [mapName, setMapName] = useState("");
  // Run title (ADR 232). The endpoint always accepted it; the form never sent one,
  // so every run read as a hex prefix in the list and the /live picker.
  const [runName, setRunName] = useState("");
  // Post-upload summaries (ADR 138): every rail reports what happened — row
  // counts always, reject details inline where there is no later page to hold
  // them (WMS/zones; the bundle's details live on the run report).
  const [result, setResult] = useState<{ runId: string; rows: number; rejected: number } | null>(null);

  // WMS expected-stock import (slice 1b, ADR 119) — a separate control next to
  // the bundle upload; reuses the same active-warehouse context, no picker of
  // its own (matches the bundle upload's convention).
  const wmsFileRef = useRef<HTMLInputElement>(null);
  const aliasFileRef = useRef<HTMLInputElement>(null);
  const [wmsBusy, setWmsBusy] = useState(false);
  const [wmsErr, setWmsErr] = useState<string | null>(null);
  const [asOf, setAsOf] = useState("");
  // ADR 383: the date is required — "today" is not when the WMS position was taken —
  // and a time is welcome, read on the site's clock by the server.
  const [asOfTime, setAsOfTime] = useState("");
  const [wmsResult, setWmsResult] = useState<{ upserted: number; merged: number; fifoDropped: number; datesDropped: number; skippedNoAddress: number; rejected: number; rejects: InventoryReject[]; duplicate: boolean; rebound: number } | null>(null);
  // Label registry (ADR 149): pallet-label -> item translation for the WMS comparison.
  const [aliasBusy, setAliasBusy] = useState(false);
  const [aliasErr, setAliasErr] = useState<string | null>(null);
  const [aliasResult, setAliasResult] = useState<{ imported: number; rejected: number } | null>(null);
  // Movements feed (ADR 377): the provisional WMS movements CSV behind the
  // velocity/ABC questions. Its own busy flag — an independent rail, not a
  // sibling of the WMS snapshot like the alias upload.
  const movementsFileRef = useRef<HTMLInputElement>(null);
  const [movementsBusy, setMovementsBusy] = useState(false);
  const [movementsErr, setMovementsErr] = useState<string | null>(null);
  const [movementsResult, setMovementsResult] = useState<{
    importId: string; duplicate: boolean; imported: number; items: number; rejected: number; rejects: InventoryReject[];
    from: string | null; to: string | null;
    byType: { pick: number; issue: number; receipt: number; transfer: number; adjust: number };
    supersededRows: number;
  } | null>(null);

  const { data: runs, isLoading, isError } = useQuery({
    queryKey: ["inventory-runs", siteId],
    queryFn: () => listInventoryRuns(siteId),
    retry: 0,
  });
  const runSort = useTableSort(runs ?? NO_RUNS, RUN_COLS);

  // Map-name suggestions (ADR 139): run.mapName must string-match an imported
  // inventory_map name or coverage/occupancy silently never resolve — a
  // datalist keeps the field free-text (runs may predate the zones import)
  // while making the correct names one click away.
  // ADR 383: the warehouse's snapshot series, newest first — what the runs bind to.
  const { data: snapshots = [] } = useQuery({
    queryKey: ["inventory-snapshots", siteId, warehouseId],
    queryFn: () => listExpectedStockSnapshots(siteId, warehouseId!),
    enabled: !!siteId && !!warehouseId,
  });
  const { data: maps = [], isSuccess: mapsLoaded } = useQuery({
    queryKey: ["inventory-maps", siteId],
    queryFn: () => listInventoryMaps(siteId),
    retry: 0,
  });

  // Maps of the warehouse being uploaded to. The query is site-wide, but a run's
  // coverage denominator is its WAREHOUSE's master (ADR 190) — a name from a sibling
  // warehouse joins nothing, so it is not a suggestion. Sites whose maps predate the
  // warehouse column keep the site-wide list rather than losing their suggestions.
  const warehouseMaps = maps.filter((m) => m.warehouseId === warehouseId);
  const suggestable = warehouseMaps.length > 0 ? warehouseMaps : maps;

  // Pre-fill from the loaded master (ADR 140): an empty field is how three unlinked
  // runs happened. Guarded adjust-during-render (not an effect) per the repo's
  // state-reset conventions; the user can still clear or retype.
  //
  // ADR 298: the latch is keyed on site+warehouse, not on "have I ever seeded". It
  // used to be a one-shot boolean, so switching site left the PREVIOUS site's map
  // name in the field — the visible symptom was a warning naming a map you had just
  // navigated away from, and the invisible one was that the next upload would have
  // sent it, which is exactly the unresolvable-denominator this field exists to
  // prevent. A value seeded for another scope is stale by definition, so a new scope
  // re-seeds over it rather than preserving it.
  const seedKey = `${siteId}:${warehouseId ?? ""}`;
  const [scope, setScope] = useState(seedKey);
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (scope !== seedKey) {
    // Nothing from the old scope may survive the switch — INCLUDING when the new
    // scope has no map at all. That case shows no warning (there is nothing to
    // mismatch against), which made it the quiet one: the field kept the previous
    // site's name and the next upload would have sent it.
    setScope(seedKey);
    setMapName("");
  }
  if (seededFor !== seedKey && suggestable.length > 0) {
    setSeededFor(seedKey);
    if (mapName === "") setMapName(suggestable[0].name);
  }

  async function handleUpload(files: File[]) {
    if (!warehouseId) return;
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = await uploadInventoryBundle(
        siteId, warehouseId, files, mapName.trim() || undefined, runName.trim() || undefined);
      setResult({ runId: r.runId, rows: r.rowCount, rejected: r.rejectedCount });
      // The title named THIS run; leaving it in the field would silently retitle
      // the next import too. The map name is a property of the master and stays.
      setRunName("");
      await qc.invalidateQueries({ queryKey: ["inventory-runs", siteId] });
    } catch (x) {
      setErr(x instanceof Error ? x.message : String(x));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleAliasUpload(file: File) {
    if (!warehouseId) return;
    setAliasBusy(true);
    setAliasErr(null);
    setAliasResult(null);
    try {
      const r = await uploadProductAliases(siteId, warehouseId, file);
      setAliasResult({ imported: r.imported, rejected: r.rejected });
    } catch (x) {
      setAliasErr(x instanceof Error ? x.message : String(x));
    } finally {
      setAliasBusy(false);
      if (aliasFileRef.current) aliasFileRef.current.value = "";
    }
  }

  async function handleWmsUpload(file: File) {
    if (!warehouseId) return;
    if (!asOf) { setWmsErr(t("inventory.wms.asOfRequired")); return; }
    setWmsBusy(true);
    setWmsErr(null);
    setWmsResult(null);
    try {
      const r = await uploadExpectedStock(siteId, warehouseId, file, asOfTime ? `${asOf}T${asOfTime}` : asOf);
      setWmsResult({ upserted: r.upserted, merged: r.merged ?? 0, fifoDropped: r.fifoDropped ?? 0, datesDropped: r.datesDropped ?? 0, skippedNoAddress: r.skippedNoAddress ?? 0, rejected: r.rejected, rejects: r.rejects ?? [], duplicate: r.duplicate ?? false, rebound: r.rebound ?? 0 });
      // The series grew and runs may have moved to the new snapshot (ADR 383).
      await qc.invalidateQueries({ queryKey: ["inventory-snapshots", siteId, warehouseId] });
      await qc.invalidateQueries({ queryKey: ["inventory-runs", siteId] });
    } catch (x) {
      setWmsErr(x instanceof Error ? x.message : String(x));
    } finally {
      setWmsBusy(false);
      if (wmsFileRef.current) wmsFileRef.current.value = "";
    }
  }

  async function handleMovementsUpload(file: File) {
    if (!warehouseId) return;
    setMovementsBusy(true);
    setMovementsErr(null);
    setMovementsResult(null);
    try {
      const r = await uploadStockMovements(siteId, warehouseId, file);
      setMovementsResult({
        importId: r.importId, duplicate: r.duplicate, imported: r.imported, items: r.items, rejected: r.rejected,
        rejects: r.rejects ?? [], from: r.from, to: r.to, byType: r.byType, supersededRows: r.supersededRows,
      });
    } catch (x) {
      setMovementsErr(x instanceof Error ? x.message : String(x));
    } finally {
      setMovementsBusy(false);
      if (movementsFileRef.current) movementsFileRef.current.value = "";
    }
  }

  const uploadDisabled = busy || !warehouseId;
  const wmsUploadDisabled = wmsBusy || !warehouseId;
  const movementsUploadDisabled = movementsBusy || !warehouseId;
  // Order-independence guards (ADR 142): the address master now lives on the
  // warehouse structure page — warn when it's missing, hint when the typed
  // name won't join it. Neither blocks the upload (reports self-heal at read
  // time; a mapless run links later via ADR 140).
  const noAddressMaster = mapsLoaded && maps.length === 0;
  const typed = mapName.trim();
  const mapNameUnknown = maps.length > 0 && typed !== "" && !maps.some((m) => m.name === typed);
  // ADR 298: the quieter half — a name that IS a real map, of a different warehouse.
  // It passes the unknown check and joins nothing, because the denominator is the
  // warehouse's master (ADR 190). Silence here is how a run gets a plausible lineage
  // string and a coverage that never resolves.
  const mapNameOtherWarehouse = !mapNameUnknown && typed !== "" && warehouseMaps.length > 0
    && !warehouseMaps.some((m) => m.name === typed);

  return (
    <div className={styles.page}>
      <div className={inv.head}>
        <h1 className={styles.h1} style={{ margin: 0 }}>{t("inventory.title")}</h1>
        {canImport && <div className={inv.uploads}>
          <div className={inv.wmsGroup}>
            <input
              type="text"
              className={inv.dateInput}
              value={runName}
              maxLength={120}
              disabled={uploadDisabled}
              onChange={(e) => setRunName(e.target.value)}
              placeholder={t("inventory.upload.runNamePlaceholder")}
              aria-label={t("inventory.upload.runNameLabel")}
            />
            <input
              type="text"
              className={inv.dateInput}
              value={mapName}
              disabled={uploadDisabled}
              onChange={(e) => setMapName(e.target.value)}
              placeholder={t("inventory.upload.mapNamePlaceholder")}
              aria-label={t("inventory.upload.mapNameLabel")}
              list="inventory-map-names"
            />
            <datalist id="inventory-map-names">
              {suggestable.map((m) => <option key={m.id} value={m.name} />)}
            </datalist>
            <label className={uploadDisabled ? `${inv.upload} ${inv.uploadDisabled}` : inv.upload}>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                multiple
                hidden
                disabled={uploadDisabled}
                onChange={(e) => {
                  const fs = Array.from(e.target.files ?? []);
                  if (fs.length > 0) handleUpload(fs);
                }}
              />
              <span>{t("inventory.upload")}</span>
            </label>
          </div>

          <div className={inv.wmsGroup}>
            <input
              type="date"
              className={inv.dateInput}
              value={asOf}
              required
              disabled={wmsUploadDisabled}
              onChange={(e) => setAsOf(e.target.value)}
              aria-label={t("inventory.wms.asOfLabel")}
            />
            <input
              type="time"
              className={inv.dateInput}
              value={asOfTime}
              disabled={wmsUploadDisabled || !asOf}
              onChange={(e) => setAsOfTime(e.target.value)}
              aria-label={t("inventory.wms.asOfTimeLabel")}
            />
            <label className={wmsUploadDisabled || !asOf ? `${inv.upload} ${inv.uploadDisabled}` : inv.upload}
                   title={!asOf ? t("inventory.wms.asOfRequired") : undefined}>
              <input
                ref={wmsFileRef}
                type="file"
                accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                disabled={wmsUploadDisabled || !asOf}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleWmsUpload(f);
                }}
              />
              <span>{t("inventory.wms.upload")}</span>
            </label>
            <label className={wmsUploadDisabled ? `${inv.upload} ${inv.uploadDisabled}` : inv.upload}>
              <input
                ref={aliasFileRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                disabled={wmsUploadDisabled || aliasBusy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleAliasUpload(f);
                }}
              />
              <span>{aliasBusy ? t("inventory.aliases.importing") : t("inventory.aliases.upload")}</span>
            </label>
          </div>

          {/* Movements feed (ADR 377) */}
          <div className={inv.wmsGroup}>
            <label className={movementsUploadDisabled ? `${inv.upload} ${inv.uploadDisabled}` : inv.upload}>
              <input
                ref={movementsFileRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                disabled={movementsUploadDisabled}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleMovementsUpload(f);
                }}
              />
              <span>{movementsBusy ? t("inventory.movements.importing") : t("inventory.movements.upload")}</span>
            </label>
          </div>

        </div>}
      </div>

      {noAddressMaster && (
        <p className={inv.uploadWarn} role="status">
          {t("inventory.noAddresses.text")}{" "}
          <Link to="/admin/warehouses">{t("inventory.noAddresses.action")}</Link>
        </p>
      )}
      {mapNameUnknown && (
        <p className={inv.uploadWarn} role="status">
          {t("inventory.upload.mapUnknownHint", { name: typed })}
        </p>
      )}
      {mapNameOtherWarehouse && (
        <p className={inv.uploadWarn} role="status">
          {t("inventory.upload.mapOtherWarehouseHint", { name: typed })}
        </p>
      )}

      {err && <p className={inv.uploadError} role="alert">{err}</p>}
      {wmsErr && <p className={inv.uploadError} role="alert">{wmsErr}</p>}
      {aliasErr && <p className={inv.uploadError} role="alert">{aliasErr}</p>}
      {movementsErr && <p className={inv.uploadError} role="alert">{movementsErr}</p>}
      {aliasResult && (
        <p className={aliasResult.rejected > 0 ? inv.uploadWarn : inv.uploadOk} role="status">
          {t("inventory.aliases.done", { imported: aliasResult.imported, rejected: aliasResult.rejected })}
        </p>
      )}

      {/* Post-upload summaries (ADR 138). The bundle's reject DETAILS live on the
          run report (persistent home); WMS has no per-import page, so its
          rejected lines render right here — never a bare count. */}
      {result && (
        <p className={result.rejected > 0 ? inv.uploadWarn : inv.uploadOk} role="status">
          {t("inventory.upload.done", { rows: result.rows, rejected: result.rejected })}{" "}
          <Link to={`/inventory/runs/${result.runId}`}>{t("inventory.upload.seeReport")}</Link>
        </p>
      )}
      {wmsResult && (
        <div className={wmsResult.rejected > 0 ? inv.uploadWarn : inv.uploadOk} role="status">
          <p>
            {t("inventory.wms.done", { upserted: wmsResult.upserted, rejected: wmsResult.rejected })}
            {wmsResult.merged > 0 && ` ${t("inventory.wms.merged", { merged: wmsResult.merged })}`}
            {wmsResult.fifoDropped > 0 && ` ${t("inventory.wms.fifoDropped", { count: wmsResult.fifoDropped })}`}
            {wmsResult.datesDropped > 0 && ` ${t("inventory.wms.datesDropped", { count: wmsResult.datesDropped })}`}
            {wmsResult.skippedNoAddress > 0 && ` ${t("inventory.wms.skippedNoAddress", { count: wmsResult.skippedNoAddress })}`}
            {wmsResult.duplicate && ` ${t("inventory.wms.duplicate")}`}
            {wmsResult.rebound > 0 && ` ${t("inventory.wms.rebound", { count: wmsResult.rebound })}`}
          </p>
          {wmsResult.rejects.length > 0 && (
            <ul className={inv.rejectList}>
              {wmsResult.rejects.map((r, i) => (
                <li key={`${r.line}-${i}`}>
                  {rejectReasonLabel(r.reason, t)} — <code>{r.line}</code>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {/* Movements feed (ADR 377). The supersede rule is said on every result:
          an import is the complete record of the days it covers, so a partial
          file for loaded days replaces them — the one surprise the rail can
          prevent. Same bytes again is a duplicate (nothing written); its counts
          and rejects are the stored import's. Dates stay ISO: they are the
          covered range the server keyed on, not a display date. */}
      {movementsResult && (
        <div className={movementsResult.rejected > 0 ? inv.uploadWarn : inv.uploadOk} role="status">
          <p>
            {movementsResult.duplicate
              ? t("inventory.movements.duplicate", { importId: movementsResult.importId })
              : t("inventory.movements.done", {
                  imported: movementsResult.imported, items: movementsResult.items,
                  from: movementsResult.from ?? "—", to: movementsResult.to ?? "—", rejected: movementsResult.rejected,
                })}
            {!movementsResult.duplicate && t("inventory.movements.byType", movementsResult.byType)}
            {movementsResult.supersededRows > 0 && t("inventory.movements.superseded", { supersededRows: movementsResult.supersededRows })}
            {t("inventory.movements.rule")}
          </p>
          {movementsResult.rejects.length > 0 && (
            <ul className={inv.rejectList}>
              {movementsResult.rejects.map((r, i) => (
                <li key={`${r.line}-${i}`}>
                  {rejectReasonLabel(r.reason, t)} — <code>{r.line}</code>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {/* ADR 383: the series every run of this warehouse binds to — newest first, the
          current one (latest WMS date) named, and how many counts read each. */}
      {warehouseId && (
        <details className={inv.series} data-testid="wms-series">
          <summary>{t("inventory.wms.series.title")}</summary>
          {snapshots.length === 0 ? (
            <p className={styles.empty}>{t("inventory.wms.series.empty")}</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("inventory.wms.series.asOf")}</th>
                  <th>{t("inventory.wms.series.file")}</th>
                  <th>{t("inventory.wms.series.rows")}</th>
                  <th>{t("inventory.wms.series.runs")}</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((sn) => (
                  <tr key={sn.importId}>
                    <td className={styles.mono}>
                      {new Date(sn.asOf).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                      {sn.current && ` (${t("inventory.wms.series.current")})`}
                    </td>
                    <td>{sn.fileName}</td>
                    <td className={styles.mono}>{sn.rowCount}</td>
                    <td className={styles.mono}>{sn.runs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </details>
      )}
      {/* ADR 363: a sweep streaming right now, with the way to the floor. */}
      <LiveCountBanner />
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <SortableTh label={t("inventory.col.name")} sortKey="name" sort={runSort.sort} onToggle={runSort.toggle} />
              <SortableTh label={t("inventory.col.addresses")} sortKey="addresses" sort={runSort.sort} onToggle={runSort.toggle} />
              <SortableTh label={t("inventory.col.status")} sortKey="status" sort={runSort.sort} onToggle={runSort.toggle} />
              <SortableTh label={t("inventory.col.imported")} sortKey="imported" sort={runSort.sort} onToggle={runSort.toggle} />
              <SortableTh label={t("inventory.col.wms")} sortKey="wms" sort={runSort.sort} onToggle={runSort.toggle} />
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 3 }, (_, i) => (
                  <tr key={i}>
                    {[160, 60, 90, 110, 80].map((w, j) => (
                      <td key={j}><span className="skeleton" style={{ width: w, height: 14, display: "block" }} /></td>
                    ))}
                  </tr>
                ))
              : isError
                ? <tr><td colSpan={5} className={styles.error}>{t("inventory.error")}</td></tr>
                : !runs?.length
                  ? <tr><td colSpan={5} className={styles.empty}>{t("inventory.empty")}</td></tr>
                  : runSort.rows.map((r) => (
                      <tr key={r.id}>
                        <td><Link to={`/inventory/runs/${r.id}`}>{r.name ?? r.id.slice(0, 8)}</Link></td>
                        <td className={styles.mono}>{r.addressScanned ?? "—"}</td>
                        <td>{t(`inventory.status.${r.status}`)}</td>
                        <td className={styles.mono}>{new Date(r.createdAt).toLocaleString("pt-BR")}</td>
                        <td className={styles.mono}>{r.expectedAsOf ? new Date(r.expectedAsOf).toLocaleDateString("pt-BR") : "—"}</td>
                      </tr>
                    ))}
          </tbody>
        </table>
      </div>
      {/* ADR 410 (was ADR 362 above the list): the sessions come first, the
          run-over-run trend summarises them below. */}
      {siteId && warehouseId && <InventoryTrendSection siteId={siteId} warehouseId={warehouseId} />}
    </div>
  );
}
